import React, { useRef, useState } from 'react';
import {
  ArrayPath,
  Control,
  FieldArrayPath,
  FieldValues,
  Path,
  useFieldArray,
  UseFormTrigger,
  useWatch,
} from 'react-hook-form';
import { Button, Card, Heading, Icon, Text } from '@legalshield/adonis-ux-framework';

export type EntityRenderers<TRow> = {
  getPrimary: (row: TRow) => string;
  getSecondary?: (row: TRow) => string | null;
  getTertiary?: (row: TRow) => string | null;
  primaryIcon?: string;
  secondaryIcon?: string;
  tertiaryIcon?: string;
};

type Props<T extends FieldValues, TRow> = {
  control: Control<T>;
  name: FieldArrayPath<T>;
  entityLabel: string;
  renderers: EntityRenderers<TRow>;
  makeEmptyRow: () => TRow;
  trigger: UseFormTrigger<T>;
  renderEditForm: (index: number) => React.ReactNode;
  isRowValid?: (row: TRow) => boolean;
};

const EntityCardList = <T extends FieldValues, TRow>({
  control,
  name,
  entityLabel,
  renderers,
  makeEmptyRow,
  trigger,
  renderEditForm,
  isRowValid,
}: Props<T, TRow>) => {
  const { fields, append, remove, update } = useFieldArray<T>({
    control,
    name: name as ArrayPath<T>,
  });

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isNewEntity, setIsNewEntity] = useState(false);
  const snapshotRef = useRef<TRow | null>(null);

  const allValues = useWatch({ control, name: name as Path<T> }) as TRow[] | undefined;

  const handleAdd = () => {
    append(makeEmptyRow() as any);
    setEditingIndex(fields.length);
    setIsNewEntity(true);
    snapshotRef.current = null;
  };

  const handleEditCard = (index: number) => {
    const row = allValues?.[index];
    snapshotRef.current = row ? (JSON.parse(JSON.stringify(row)) as TRow) : null;
    setEditingIndex(index);
    setIsNewEntity(false);
  };

  const handleCancel = () => {
    if (isNewEntity && editingIndex !== null) {
      remove(editingIndex);
    } else if (editingIndex !== null && snapshotRef.current !== null) {
      update(editingIndex, snapshotRef.current as any);
      void trigger();
    }
    snapshotRef.current = null;
    setEditingIndex(null);
    setIsNewEntity(false);
  };

  const handleSave = async () => {
    if (editingIndex === null) return;
    const ok = await trigger(`${String(name)}.${editingIndex}` as Path<T>);
    if (ok) {
      snapshotRef.current = null;
      setEditingIndex(null);
      setIsNewEntity(false);
    }
  };

  const handleDelete = () => {
    if (editingIndex === null) return;
    remove(editingIndex);
    snapshotRef.current = null;
    setEditingIndex(null);
    setIsNewEntity(false);
  };

  const renderCard = (row: TRow, index: number) => {
    const primary = renderers.getPrimary(row);
    const secondary = renderers.getSecondary?.(row) ?? null;
    const tertiary = renderers.getTertiary?.(row) ?? null;
    return (
      <Card
        key={`${String(name)}-card-${index}`}
        classNames={['mb-3']}
        padding={4}
        onClick={() => handleEditCard(index)}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {renderers.primaryIcon ? <Icon name={renderers.primaryIcon} classNames={['mr-2']} /> : null}
              <Heading as="T16" text={primary || '—'} />
            </div>
            {secondary ? (
              <div className="mt-2" style={{ display: 'flex', alignItems: 'center' }}>
                {renderers.secondaryIcon ? (
                  <Icon name={renderers.secondaryIcon} size="small" classNames={['mr-2']} />
                ) : null}
                <Text style={{ fontSize: 14 }} text={secondary} />
              </div>
            ) : null}
            {tertiary ? (
              <div className="mt-2" style={{ display: 'flex', alignItems: 'center' }}>
                {renderers.tertiaryIcon ? (
                  <Icon name={renderers.tertiaryIcon} size="small" classNames={['mr-2']} />
                ) : null}
                <Text style={{ fontSize: 14 }} text={tertiary} />
              </div>
            ) : null}
          </div>
          <div
            style={{
              backgroundColor: '#EEEEEE',
              width: 40,
              height: 40,
              borderRadius: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="action_edit" />
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="rnq-entity-list">
      {fields.map((f, index) => {
        if (index === editingIndex) return null;
        const row = allValues?.[index] as TRow | undefined;
        if (!row) return null;
        return <React.Fragment key={f.id}>{renderCard(row, index)}</React.Fragment>;
      })}

      {editingIndex === null && (
        <Button
          label={`+ Add ${entityLabel}`}
          variant="standard"
          onClick={handleAdd}
          classNames={['rnq-entity-list__add']}
        />
      )}

      {editingIndex !== null && (
        <div className="rnq-entity-edit">
          <Heading as="T20" text={`${isNewEntity ? 'Add' : 'Update'} ${entityLabel}`} classNames={['mb-3']} />
          <div className="rnq-field-group">{renderEditForm(editingIndex)}</div>
          <div className="rnq-entity-edit__actions mt-4" style={{ display: 'flex' }}>
            <Button label="Cancel" variant="standard" onClick={handleCancel} />
            {!isNewEntity ? (
              <Button
                label={`Delete ${entityLabel}`}
                variant="destructive"
                onClick={handleDelete}
                classNames={['ml-3']}
              />
            ) : null}
            <Button
              label={`${isNewEntity ? 'Add' : 'Update'} ${entityLabel}`}
              variant="primary"
              onClick={handleSave}
              classNames={['ml-3']}
              disabled={isRowValid && editingIndex !== null ? !isRowValid(allValues?.[editingIndex] as TRow) : false}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EntityCardList;
